<template>
  <div>
    <!-- Encabezado de sección y botón de consulta en Meta -->
    <div class="row align-items-center mb-3 g-2">
      <div class="col-md-8 col-12">
        <h5 class="font-size-15 mb-1 text-primary">
          <i class="mdi mdi-file-document-edit-outline me-1"></i> Plantillas Oficiales de WhatsApp
        </h5>
        <p class="text-muted font-size-13 mb-0">
          Configure y sincronice las plantillas oficiales con Meta WhatsApp Cloud API.
        </p>
      </div>
      <div class="col-md-4 col-12 text-md-end">
        <button
          type="button"
          class="btn btn-outline-primary btn-sm d-inline-flex align-items-center shadow-sm"
          @click="$emit('consultar-meta')"
          :disabled="banderaConsultar"
          title="Consulta las plantillas registradas en Meta WhatsApp Cloud API"
        >
          <i class="mdi me-1" :class="banderaConsultar ? 'mdi-concourse-ci mdi-spin' : 'mdi-cloud-sync-outline'"></i>
          {{ banderaConsultar ? 'Consultando...' : 'Consultar Estado en Meta' }}
        </button>
      </div>
    </div>

    <!-- Alerta si el servicio general está desactivado -->
    <div class="alert alert-warning border-0 shadow-sm d-flex align-items-center mb-3" v-if="!ajustes.whatsapp_activo">
      <i class="mdi mdi-alert-circle font-size-22 me-2 text-warning"></i>
      <div class="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
        <span class="font-size-13">
          <strong>Servicio general en pausa:</strong> Active el servicio en <a href="javascript:void(0);" class="alert-link fw-bold" @click="$emit('ir-a-configuracion')">Configuración y API</a> para sincronizar con Meta y enviar notificaciones.
        </span>
        <button
          type="button"
          class="btn btn-warning btn-sm py-1 px-2 font-size-12 shadow-sm"
          @click="$emit('habilitar-servicio')"
        >
          <i class="mdi mdi-power me-1"></i> Activar WhatsApp API
        </button>
      </div>
    </div>

    <!-- Selector de Plantillas (Tarjetas) -->
    <div class="row g-3 mb-4" v-if="plantillas && plantillas.length > 0">
      <div
        class="col-12 col-md-6"
        v-for="(tpl, idx) in plantillas"
        :key="tpl.id"
      >
        <div
          class="card mb-0 border style-pointer transition-all h-100"
          :class="{
            'border-primary shadow-sm border-2': plantillaSeleccionadaIdx === idx,
            'opacity-75': plantillaSeleccionadaIdx !== idx
          }"
          @click="$emit('seleccionar-plantilla', idx)"
        >
          <div class="card-body p-3 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center me-2 text-truncate">
              <div
                class="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                :class="plantillaSeleccionadaIdx === idx ? 'bg-primary bg-opacity-10 text-primary' : 'bg-secondary bg-opacity-10 text-secondary'"
                style="width: 42px; height: 42px;"
              >
                <i :class="obtenerIcono(tpl)" class="font-size-22"></i>
              </div>
              <div class="text-truncate">
                <div class="fw-bold font-size-14 text-truncate d-flex align-items-center">
                  {{ tpl.titulo }}
                  <span class="badge bg-primary ms-2 font-size-10" v-if="plantillaSeleccionadaIdx === idx">Seleccionada</span>
                </div>
                <div class="text-muted font-size-12 text-truncate mt-1">
                  Template: <code>{{ tpl.nombre_meta }}</code>
                  <span class="badge bg-secondary bg-opacity-10 text-body border ms-1 font-monospace">{{ tpl.idioma || 'es_MX' }}</span>
                  <span class="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle ms-1 font-size-10">{{ tpl.categoria || 'UTILITY' }}</span>
                  <span class="badge bg-info bg-opacity-10 text-info border border-info-subtle ms-1" v-if="tpl.tipo_encabezado === 'IMAGE'">
                    <i class="mdi mdi-image-outline me-1"></i>Con Imagen
                  </span>
                  <span class="badge bg-secondary bg-opacity-10 text-secondary border ms-1" v-else>
                    <i class="mdi mdi-text-box-outline me-1"></i>Solo Texto
                  </span>
                </div>
              </div>
            </div>

            <!-- Estado Meta -->
            <div>
              <span
                class="badge font-size-11 px-2 py-1 shadow-sm"
                :class="{
                  'bg-success': tpl.estado_meta === 'APPROVED',
                  'bg-warning text-dark': tpl.estado_meta === 'PENDING',
                  'bg-danger': tpl.estado_meta === 'REJECTED',
                  'bg-secondary': !tpl.estado_meta || tpl.estado_meta === 'NO_REGISTRADA'
                }"
              >
                <i class="mdi me-1" :class="{
                  'mdi-check-decagram': tpl.estado_meta === 'APPROVED',
                  'mdi-clock-outline': tpl.estado_meta === 'PENDING',
                  'mdi-alert-circle': tpl.estado_meta === 'REJECTED',
                  'mdi-cloud-outline': !tpl.estado_meta || tpl.estado_meta === 'NO_REGISTRADA'
                }"></i>
                Meta: {{ etiquetaEstadoMeta(tpl.estado_meta) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Editor de la Plantilla Seleccionada -->
    <div class="row g-4" v-if="localPlantilla">
      <!-- Columna Izquierda: Formulario -->
      <div class="col-lg-7">
        <div class="card border">
          <div class="card-header d-flex justify-content-between align-items-center py-2 flex-wrap gap-2">
            <div class="fw-bold d-flex align-items-center flex-wrap gap-2">
              <i :class="obtenerIcono(localPlantilla)" class="me-1 text-primary font-size-18"></i>
              <span>{{ localPlantilla.titulo }}</span>

              <span
                class="badge font-size-11"
                :class="{
                  'bg-success': localPlantilla.estado_meta === 'APPROVED',
                  'bg-warning text-dark': localPlantilla.estado_meta === 'PENDING',
                  'bg-danger': localPlantilla.estado_meta === 'REJECTED',
                  'bg-secondary': !localPlantilla.estado_meta || localPlantilla.estado_meta === 'NO_REGISTRADA'
                }"
              >
                <i class="mdi me-1" :class="{
                  'mdi-check-decagram': localPlantilla.estado_meta === 'APPROVED',
                  'mdi-clock-outline': localPlantilla.estado_meta === 'PENDING',
                  'mdi-alert-circle': localPlantilla.estado_meta === 'REJECTED',
                  'mdi-cloud-outline': !localPlantilla.estado_meta || localPlantilla.estado_meta === 'NO_REGISTRADA'
                }"></i>
                Meta: {{ etiquetaEstadoMeta(localPlantilla.estado_meta) }}
              </span>
            </div>
          </div>

          <div class="card-body p-3">
            <fieldset class="border-0 p-0 m-0" :disabled="!ajustes.whatsapp_activo">
              <!-- Datos Meta: Nombre, Idioma, Categoría -->
              <div class="row g-3 mb-3">
                <div class="col-md-6">
                  <label class="form-label fw-semibold font-size-13 mb-1">Nombre en Meta</label>
                  <input
                    type="text"
                    class="form-control form-control-sm font-monospace"
                    v-model="localPlantilla.nombre_meta"
                    disabled
                    readonly
                  />
                </div>

                <div class="col-md-3">
                  <label class="form-label fw-semibold font-size-13 mb-1">Idioma <span class="text-danger">*</span></label>
                  <select
                    class="form-select form-select-sm"
                    v-model="localPlantilla.idioma"
                    @change="alCambiarIdiomaLocal"
                  >
                    <optgroup label="Español">
                      <option value="es_MX">Español (México) - es_MX</option>
                      <option value="es_ES">Español (España) - es_ES</option>
                      <option value="es_AR">Español (Argentina) - es_AR</option>
                      <option value="es_CO">Español (Colombia) - es_CO</option>
                      <option value="es_CL">Español (Chile) - es_CL</option>
                      <option value="es">Español (Genérico) - es</option>
                    </optgroup>
                    <optgroup label="Inglés">
                      <option value="en_US">Inglés (EE. UU.) - en_US</option>
                      <option value="en_GB">Inglés (Reino Unido) - en_GB</option>
                      <option value="en">Inglés (Genérico) - en</option>
                    </optgroup>
                  </select>
                </div>

                <div class="col-md-3">
                  <label class="form-label fw-semibold font-size-13 mb-1">Categoría <span class="text-danger">*</span></label>
                  <select
                    class="form-select form-select-sm font-monospace"
                    v-model="localPlantilla.categoria"
                  >
                    <option value="UTILITY">UTILITY (Transaccional)</option>
                    <option value="MARKETING">MARKETING (Promocional/Contacto)</option>
                  </select>
                </div>
              </div>

              <!-- Tipo de encabezado para inicio_conversacion -->
              <div class="row g-3 mb-3" v-if="localPlantilla.id === 'inicio_conversacion'">
                <div class="col-md-4">
                  <label class="form-label fw-semibold font-size-13 mb-1">Tipo de Encabezado</label>
                  <select
                    class="form-select form-select-sm"
                    v-model="localPlantilla.tipo_encabezado"
                  >
                    <option value="NONE">Sin Encabezado</option>
                    <option value="TEXT">Texto Fijo</option>
                  </select>
                </div>
                <div class="col-md-8" v-if="localPlantilla.tipo_encabezado === 'TEXT'">
                  <label class="form-label fw-semibold font-size-13 mb-1">Texto del Encabezado</label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="localPlantilla.encabezado"
                    placeholder="Ej. Aviso importante"
                    maxlength="60"
                  />
                </div>
              </div>

              <!-- Encabezado con imagen solo si tipo_encabezado === 'IMAGE' -->
              <div class="mb-3" v-if="localPlantilla.tipo_encabezado === 'IMAGE'">
                <label class="form-label font-size-12 fw-semibold text-muted mb-1">
                  URL de Imagen de Muestra / Comprobante
                </label>
                <div class="input-group input-group-sm">
                  <span class="input-group-text"><i class="mdi mdi-image-outline"></i></span>
                  <input
                    type="text"
                    class="form-control font-monospace"
                    v-model="localPlantilla.url_imagen_encabezado"
                    placeholder="https://..."
                  />
                </div>
                <small class="text-muted font-size-11 d-block mt-1">
                  Meta requiere una imagen de muestra para el registro.
                </small>
              </div>

              <!-- Selector de Variables Disponibles -->
              <div class="mb-3">
                <div
                  v-for="(grupo, idx) in gruposVariablesFiltrados"
                  :key="grupo.categoria"
                  :class="{ 'mb-2': idx < gruposVariablesFiltrados.length - 1 }"
                >
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <label class="form-label fw-bold font-size-13 mb-0">
                      <i :class="grupo.icono + ' text-primary me-1'"></i> {{ grupo.categoria }}
                    </label>
                  </div>
                  <div class="d-flex flex-wrap gap-1">
                    <button
                      type="button"
                      class="btn btn-sm py-0 px-2 font-size-11"
                      :class="grupo.btnClass"
                      v-for="v in grupo.variables"
                      :key="v.codigo"
                      @click="insertarVariable(v.codigo)"
                      :title="v.descripcion + ' — Clic para insertar ' + v.codigo"
                    >
                      + {{ v.codigo }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Cuerpo del Mensaje (Body) -->
              <div class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <label class="form-label fw-bold font-size-13 mb-0">
                    <i class="mdi mdi-text-box-outline text-primary me-1"></i> Cuerpo del Mensaje (Body) <span class="text-danger">*</span>
                  </label>
                  <span class="badge bg-secondary bg-opacity-10 text-body border font-size-11">
                    {{ localPlantilla.cuerpo ? localPlantilla.cuerpo.length : 0 }}/1024 caracteres
                  </span>
                </div>
                <textarea
                  class="form-control font-monospace font-size-13"
                  ref="txtCuerpo"
                  rows="5"
                  v-model="localPlantilla.cuerpo"
                  placeholder="Escriba el texto del mensaje..."
                  maxlength="1024"
                  :class="{ 'is-invalid border-danger': cuerpoTerminaEnVariable }"
                ></textarea>

                <!-- Advertencia regla de Meta: no terminar en variable -->
                <div
                  class="alert alert-danger py-2 px-3 mt-2 mb-1 font-size-12 d-flex align-items-center justify-content-between shadow-sm"
                  v-if="cuerpoTerminaEnVariable"
                >
                  <span>
                    <i class="mdi mdi-alert-circle me-1"></i>
                    <strong>Regla de Meta:</strong> El mensaje no puede terminar en variable. Agregue un punto al final.
                  </span>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger py-0 px-2 font-size-11"
                    @click="agregarPuntoAlFinal()"
                  >
                    <i class="mdi mdi-check me-1"></i> Agregar punto
                  </button>
                </div>
              </div>

              <!-- Pie de Página (Footer Opcional) solo para confirmacion_pago -->
              <div class="mb-2" v-if="localPlantilla.id !== 'inicio_conversacion'">
                <label class="form-label fw-bold font-size-13 mb-1">
                  <i class="mdi mdi-page-layout-footer text-primary me-1"></i> Pie de Página (Footer Opcional)
                </label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="localPlantilla.pie"
                  placeholder="Ej. Comprobante emitido por el sistema."
                  maxlength="60"
                  :class="{ 'is-invalid border-danger': pieTieneVariables }"
                />

                <div
                  class="alert alert-danger py-2 px-3 mt-2 mb-1 font-size-12 d-flex align-items-center justify-content-between shadow-sm"
                  v-if="pieTieneVariables"
                >
                  <span>
                    <i class="mdi mdi-alert-circle me-1"></i>
                    <strong>Regla de Meta:</strong> El pie de página solo admite texto fijo sin variables.
                  </span>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger py-0 px-2 font-size-11"
                    @click="removerVariablesDePie()"
                  >
                    Quitar variables
                  </button>
                </div>
              </div>
            </fieldset>

            <!-- Notificación de Respuesta de Meta -->
            <div class="mt-3" v-if="respuestaMeta">
              <div
                class="alert border-0 shadow-sm d-flex align-items-start justify-content-between mb-0"
                :class="respuestaMeta.error ? 'alert-danger' : 'alert-success'"
              >
                <div class="d-flex align-items-start">
                  <i
                    class="mdi me-2 font-size-20 flex-shrink-0"
                    :class="respuestaMeta.error ? 'mdi-alert-circle text-danger' : 'mdi-check-circle text-success'"
                  ></i>
                  <div class="font-size-13">
                    <div class="fw-bold">
                      {{ respuestaMeta.error ? 'Error en Meta API' : '¡Plantilla registrada en Meta API!' }}
                    </div>
                    <div v-if="respuestaMeta.error" class="mt-1">
                      {{ respuestaMeta.error.error_user_msg || respuestaMeta.error.message || respuestaMeta.error }}
                    </div>
                    <div v-else class="mt-1">
                      ID: <code>{{ respuestaMeta.id }}</code> | Estado: <strong>{{ respuestaMeta.status || 'PENDING' }}</strong>
                    </div>
                  </div>
                </div>
                <button type="button" class="btn-close" @click="$emit('limpiar-respuesta-meta')"></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Derecha: Vista Previa WhatsApp y Reglas Clave -->
      <div class="col-lg-5">
        <div class="card border shadow-sm">
          <div class="card-body p-3 d-flex justify-content-center bg-secondary bg-opacity-10">
            <CmpPreviewMockupWhatsapp
              :plantilla="localPlantilla"
              :activo="ajustes.whatsapp_activo"
              :nombre-empresa="nombreEmpresa"
            />
          </div>
        </div>

        <!-- Guía Rápida de Reglas de Meta -->
        <div class="card border shadow-sm mt-3">
          <div class="card-header py-2 px-3 d-flex align-items-center justify-content-between">
            <span class="fw-bold font-size-13 text-primary">
              <i class="mdi mdi-shield-check-outline me-1"></i> Reglas de Meta WhatsApp API
            </span>
            <span class="badge bg-secondary bg-opacity-10 text-body border font-size-10">Oficial</span>
          </div>
          <div class="card-body p-3 font-size-12">
            <ul class="list-unstyled mb-0 d-flex flex-column gap-2 text-muted">
              <li><i class="mdi mdi-check text-success me-1"></i> El cuerpo del mensaje <strong>no debe terminar en variable</strong> (agregue un punto).</li>
              <li><i class="mdi mdi-check text-success me-1"></i> El pie de página <strong>no admite variables</strong> dinámicas (solo texto fijo).</li>
              <li><i class="mdi mdi-check text-success me-1"></i> Use <strong>UTILITY</strong> para confirmaciones de pago y <strong>MARKETING</strong> o UTILITY para saludos.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Barra Inferior de Acciones de Plantillas -->
    <hr class="mt-4 mb-3">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2" v-if="localPlantilla">
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-secondary" @click="$router.go(-1)">
          <i class="mdi mdi-chevron-left me-1"></i> Atrás
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="$emit('restablecer')"
          :disabled="!ajustes.whatsapp_activo"
          title="Restablece esta plantilla al formato predeterminado"
        >
          <i class="mdi mdi-restore me-1"></i> Restablecer plantilla
        </button>
      </div>

      <div class="d-flex align-items-center gap-2">
        <!-- Botón para Guardar la configuración y plantillas en el sistema -->
        <button
          type="button"
          class="btn btn-outline-primary d-inline-flex align-items-center shadow-sm"
          @click="clickGuardarPlantillas()"
          :disabled="banderaSpinner || !ajustes.whatsapp_activo"
          title="Guarda la configuración y las plantillas en el servidor"
        >
          <i class="mdi me-1" :class="banderaSpinner ? 'mdi-concourse-ci mdi-spin' : 'mdi-content-save'"></i>
          {{ banderaSpinner ? 'Guardando...' : 'Guardar Plantillas' }}
        </button>

        <button
          type="button"
          class="btn btn-outline-danger d-inline-flex align-items-center"
          v-if="localPlantilla.estado_meta && localPlantilla.estado_meta !== 'NO_REGISTRADA'"
          @click="$emit('eliminar-meta', localPlantilla)"
          :disabled="banderaEliminando === (localPlantilla.nombre_meta || localPlantilla.id) || !ajustes.whatsapp_activo"
          title="Elimina esta plantilla registrada de Meta WhatsApp Cloud API"
        >
          <i class="mdi me-1" :class="banderaEliminando === (localPlantilla.nombre_meta || localPlantilla.id) ? 'mdi-concourse-ci mdi-spin' : 'mdi-trash-can-outline'"></i>
          {{ banderaEliminando === (localPlantilla.nombre_meta || localPlantilla.id) ? 'Eliminando...' : 'Eliminar en Meta API' }}
        </button>

        <button
          type="button"
          class="btn btn-primary d-inline-flex align-items-center"
          v-if="localPlantilla.estado_meta && localPlantilla.estado_meta !== 'NO_REGISTRADA'"
          @click="clickActualizarMeta()"
          :disabled="banderaActualizar || !ajustes.whatsapp_activo"
          title="Sincroniza y actualiza las modificaciones de la plantilla en Meta Cloud API"
        >
          <i class="mdi me-1" :class="banderaActualizar ? 'mdi-concourse-ci mdi-spin' : 'mdi-cloud-sync'"></i>
          {{ banderaActualizar ? 'Actualizando...' : 'Actualizar en Meta API' }}
        </button>

        <button
          type="button"
          class="btn btn-success d-inline-flex align-items-center"
          v-if="!localPlantilla.estado_meta || localPlantilla.estado_meta === 'NO_REGISTRADA'"
          @click="clickCrearMeta()"
          :disabled="banderaCrear || !ajustes.whatsapp_activo"
          title="Envía POST a api/sitios/crear-plantilla-whatsapp para registrar la plantilla en Meta Cloud API"
        >
          <i class="mdi me-1" :class="banderaCrear ? 'mdi-concourse-ci mdi-spin' : 'mdi-cloud-upload'"></i>
          {{ banderaCrear ? 'Creando...' : 'Crear en Meta API' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CmpPreviewMockupWhatsapp from './CmpPreviewMockupWhatsapp.vue'
import { etiquetaEstadoMeta } from './whatsappConstants.js'

export default {
  name: 'CmpPlantillasWhatsapp',
  components: {
    CmpPreviewMockupWhatsapp
  },
  props: {
    ajustes: { type: Object, required: true },
    plantillas: { type: Array, default: () => [] },
    plantilla: { type: Object, default: null },
    plantillaSeleccionadaIdx: { type: Number, default: 0 },
    gruposVariables: { type: Array, default: () => [] },
    banderaSpinner: { type: Boolean, default: false },
    banderaCrear: { type: Boolean, default: false },
    banderaActualizar: { type: Boolean, default: false },
    banderaConsultar: { type: Boolean, default: false },
    banderaEliminando: { type: [String, Number], default: null },
    respuestaMeta: { type: Object, default: null },
    nombreEmpresa: { type: String, default: 'ArgusBlack ISP' }
  },
  data() {
    return {
      localPlantilla: this.plantilla ? Object.assign({}, this.plantilla) : null
    }
  },
  watch: {
    plantilla: {
      handler(val) {
        this.localPlantilla = val ? Object.assign({}, val) : null
      },
      deep: true
    },
    localPlantilla: {
      handler(val) {
        if (val) this.$emit('update-plantilla', val)
      },
      deep: true
    }
  },
  computed: {
    pieTieneVariables() {
      return this.localPlantilla && this.localPlantilla.pie && /\{\{[^}]+\}\}/.test(this.localPlantilla.pie)
    },
    cuerpoTerminaEnVariable() {
      return this.localPlantilla && this.localPlantilla.cuerpo && /\{\{[^}]+\}\}\s*$/.test(this.localPlantilla.cuerpo)
    },
    gruposVariablesFiltrados() {
      if (!this.gruposVariables || !Array.isArray(this.gruposVariables)) return []
      if (this.localPlantilla && this.localPlantilla.id === 'inicio_conversacion') {
        return this.gruposVariables.filter(g => g.categoria !== 'Datos del ticket de pago')
      }
      return this.gruposVariables
    }
  },
  methods: {
    etiquetaEstadoMeta,

    alCambiarIdiomaLocal() {
      this.$emit('update-plantilla', this.localPlantilla)
      this.$nextTick(() => {
        this.$emit('cambiar-idioma', this.localPlantilla.idioma)
      })
    },

    insertarVariable(variable) {
      let txt = this.$refs.txtCuerpo
      let actual = (this.localPlantilla && this.localPlantilla.cuerpo) || ''
      if (!txt) {
        if (this.localPlantilla) this.localPlantilla.cuerpo = (actual ? actual + ' ' : '') + variable + '.'
        return
      }
      let inicio = txt.selectionStart
      let fin = txt.selectionEnd
      let textoPosterior = actual.substring(fin)
      let textoAInsertar = !textoPosterior.trim() ? variable + '.' : variable

      if (this.localPlantilla) {
        this.localPlantilla.cuerpo = actual.substring(0, inicio) + textoAInsertar + textoPosterior
      }
      this.$nextTick(() => {
        txt.focus()
        let pos = inicio + textoAInsertar.length
        txt.selectionStart = txt.selectionEnd = pos
      })
    },

    agregarPuntoAlFinal() {
      if (!this.localPlantilla || !this.localPlantilla.cuerpo) return
      let texto = this.localPlantilla.cuerpo.trim()
      if (/\{\{[^}]+\}\}$/.test(texto)) {
        this.localPlantilla.cuerpo = texto + '.'
        iu.msg.success('Punto agregado al final')
      }
    },

    removerVariablesDePie() {
      if (this.localPlantilla && this.localPlantilla.pie) {
        this.localPlantilla.pie = this.localPlantilla.pie.replace(/\{\{[^}]+\}\}/g, '').trim() || 'Comprobante emitido por el sistema'
        iu.msg.info('Variables removidas del pie de página.')
      }
    },

    clickGuardarPlantillas() {
      if (!this.localPlantilla) return
      if (this.cuerpoTerminaEnVariable) {
        this.agregarPuntoAlFinal()
      }
      if (this.pieTieneVariables) {
        this.removerVariablesDePie()
      }
      this.$emit('guardar-plantillas', this.localPlantilla)
    },

    clickCrearMeta() {
      if (!this.localPlantilla) return
      if (this.cuerpoTerminaEnVariable) {
        this.agregarPuntoAlFinal()
      }
      if (this.pieTieneVariables) {
        this.removerVariablesDePie()
      }
      if (!this.localPlantilla.cuerpo) {
        iu.msg.warning('El cuerpo del mensaje no puede estar vacío.')
        return
      }
      this.$emit('crear-meta', this.localPlantilla)
    },

    clickActualizarMeta() {
      if (!this.localPlantilla) return
      if (this.cuerpoTerminaEnVariable) {
        this.agregarPuntoAlFinal()
      }
      if (this.pieTieneVariables) {
        this.removerVariablesDePie()
      }
      if (!this.localPlantilla.cuerpo) {
        iu.msg.warning('El cuerpo del mensaje no puede estar vacío.')
        return
      }
      this.$emit('actualizar-meta', this.localPlantilla)
    },

    obtenerIcono(tpl) {
      if (!tpl) return 'mdi mdi-file-document-outline'
      if (tpl.id === 'confirmacion_pago' || tpl.nombre_meta === 'confirmacion_pago') return 'mdi mdi-receipt'
      if (tpl.id === 'inicio_conversacion' || tpl.nombre_meta === 'inicio_conversacion') return 'mdi mdi-message-text-outline'
      return tpl.icono || 'mdi mdi-file-document-outline'
    }
  }
}
</script>

<style scoped>
.style-pointer {
  cursor: pointer;
}
.transition-all {
  transition: all 0.2s ease-in-out;
}
</style>
